import React, {useEffect, useRef, useState} from 'react';
import useIndentUnderTab from './useIndentUnderTab';
import {OUTLINED_BOX_SX, TopLineAroundTab} from './tabOutline';
import PropTypes from 'prop-types';
import {Box, Button, Card, CircularProgress, Collapse, Dialog, IconButton, Menu, MenuItem, Popover, Typography, useMediaQuery, useTheme} from '@mui/material';
import {ArrowBack, Close, ExpandLess, ExpandMore, MoreVert} from '@mui/icons-material';
import {downloadPdf, printPdf} from './formPdf';
import {Route, Routes} from 'react-router-dom';
import SubmitPrivateForm from '../../../RequestAndResponses/components/PrivateForms/SubmitPrivateForm';
import AdmicityStatus from '../../../../../shared-components/AdmicityStatus';
import {getApprovalStatusInfo} from '../../../../../constants/FormApprovalStatuses';
import {formatDate} from '../../../../../utility/dateUtil';

// Maria, 18 October 2026: the Self-service Forms tab of a profile, laid out so it is clear how the forms work. Each form
// has an 'Open' button and a 'Submissions' button; the
// submissions open underneath the form, each with a 'View' button.

// Maria, 18 October 2026: the lines between the forms slightly darker, so they are easier to see, and the list only
// as wide as the form names and buttons, ending in line with the last tab when the tabs are wider
const LINE = '1px solid #dde2e8';
// Maria, 19 October 2026: the buttons are only as wide as their words
const OPEN_BUTTON_SIZE = {minWidth: 60, px: 1.25, height: 30.75, whiteSpace: 'nowrap', flexShrink: 0};

const linkedPersonQuery = form => {
    if (form?.linkedStudentId) return `?linkedStudentId=${form.linkedStudentId}`;
    if (form?.linkedEmployeeId) return `?linkedEmployeeId=${form.linkedEmployeeId}`;
    return '';
};

// A self-service form only has a submission once it has been submitted, so there is no status column
// The Approval column only shows for a form with approval stages. Maria, 18 October 2026: the table only as wide as
// its columns
// Maria, 18 October 2026: on a staff profile every self-service submission is the member of staff's own, so there is
// no 'Submitted By' column
// Maria, 18 October 2026: 'View' close to the date
// AD-2751: on a phone the name takes the spare width, so 'View' and its menu stay on the screen
const submissionColumns = (withApproval, withSubmittedBy, isPhone = false) => ['max-content', withSubmittedBy && (isPhone ? 'minmax(0, 1fr)' : '200px'), withApproval && (isPhone ? 'max-content' : '180px'), 'max-content']
    .filter(Boolean).join(' ');

// Maria, 18 October 2026: 'View' opens the submitted form in a panel just to the right of the button, over the page.
// The panel shows the form itself, with the school logo, as it looks when it is opened; 'Download' and 'Print' use
// the form's PDF.
const PREVIEW_ROUTE = 'preview/:formRequestId';
const personalForm = ({user}) => !user.hasElevatedAccess();

// AD-2751 (Maria, 22 October 2026): on a phone 'View' opens the form on the whole page, with 'Back' and the form name
// and date at the top. 'Download' and 'Print' are in a menu next to 'View'. Tablet and desktop are unchanged.
const usePhone = () => {
    const theme = useTheme();
    return useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
};

export const PdfMenu = ({formId, responseId, name}) => {
    const [anchor, setAnchor] = useState(null);
    const run = action => async event => {
        event.stopPropagation();
        setAnchor(null);
        await action(formId, responseId, name ?? 'Submitted form');
    };
    return (
        <>
            <IconButton
                size="small"
                aria-label={`More actions for ${name ?? 'this form'}`}
                data-testid="form-pdf-menu"
                onClick={event => {
                    event.stopPropagation();
                    setAnchor(event.currentTarget);
                }}
            >
                <MoreVert fontSize="small"/>
            </IconButton>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={event => event.stopPropagation()}>
                <MenuItem onClick={run(downloadPdf)}>Download</MenuItem>
                <MenuItem onClick={run(printPdf)}>Print</MenuItem>
            </Menu>
        </>
    );
};

PdfMenu.propTypes = {
    formId: PropTypes.number,
    responseId: PropTypes.number,
    name: PropTypes.string
};

const ResponseForm = ({response}) => (
    <Routes location={`${window.location.pathname.replace(/\/$/, '')}/preview/${response.id}`}>
        <Route path={PREVIEW_ROUTE} element={<SubmitPrivateForm navigateBackRoute="." personalForm={personalForm}/>}/>
    </Routes>
);

ResponseForm.propTypes = {
    response: PropTypes.object.isRequired
};

// AD-2759 (David, 23 October 2026; Maria: phones only): on a phone there is no 'View' button. Tapping a submitted
// form's, a submission's or a communication's row opens it on the full page. Each row's viewer registers how it is
// opened, so the table's row click can open it. Tablets and computers keep 'View' (Maria, AD-2725).
const viewerOpeners = new Map();
const useViewerOpener = (key, open) => {
    const openRef = useRef(open);
    openRef.current = open;
    useEffect(() => {
        if (key == null) return undefined;
        const opener = () => openRef.current();
        viewerOpeners.set(key, opener);
        return () => {
            if (viewerOpeners.get(key) === opener) viewerOpeners.delete(key);
        };
    }, [key]);
};
export const openViewer = (key) => {
    const opener = viewerOpeners.get(key);
    if (!opener) return false;
    opener();
    return true;
};

export const SubmissionPanel = ({anchorEl, response, formId, onClose, showSubmittedBy, title, phoneTitle}) => {
    const [busy, setBusy] = useState(null);
    const isPhone = usePhone();
    const open = !!anchorEl && !!response;
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    // on a phone the form is a page of its own, so the phone's back gesture and the browser's Back button return to the list
    useEffect(() => {
        if (!isPhone || !open) return undefined;
        window.history.pushState(window.history.state, '');
        const onBack = () => closeRef.current();
        window.addEventListener('popstate', onBack);
        return () => window.removeEventListener('popstate', onBack);
    }, [isPhone, open]);

    const run = (kind, action) => async () => {
        setBusy(kind);
        await action(formId, response.id, response.formName ?? 'Submitted form');
        setBusy(null);
    };
    const download = run('download', downloadPdf);
    const print = run('print', printPdf);
    const dateLine = <>{response?.editedAt ? formatDate(response.editedAt) : ''}{showSubmittedBy && response?.editedBy?.fullname ? ` - ${response.editedBy.fullname}` : ''}</>;

    if (isPhone) {
        return (
            <Dialog fullScreen open={open} onClose={() => window.history.back()} data-testid="form-page">
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: LINE}}>
                    <Button size="small" startIcon={<ArrowBack/>} onClick={() => window.history.back()} sx={{flexShrink: 0}}>
                        Back
                    </Button>
                    <Typography sx={{fontWeight: 600, lineHeight: 1.3, minWidth: 0, overflowWrap: 'anywhere'}}>
                        {phoneTitle ?? title ?? dateLine}
                    </Typography>
                </Box>
                <Box sx={{flex: 1, overflowY: 'auto', backgroundColor: '#f6f8fa', px: 1, py: 1}}>
                    {response && <ResponseForm response={response}/>}
                </Box>
            </Dialog>
        );
    }

    return (
        <Popover
            open={!!anchorEl && !!response}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'center', horizontal: 'right'}}
            transformOrigin={{vertical: 'center', horizontal: 'left'}}
            slotProps={{paper: {sx: {ml: 1, borderRadius: 2, width: 'min(760px, 60vw)', height: '75vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}}}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: LINE}}>
                <Typography sx={{flex: 1, fontWeight: 600}}>
                    {/* Maria, 18 October 2026: the date, without the word 'Submitted' */}
                    {title ?? dateLine}
                </Typography>
                <Button size="small" variant="text" onClick={download} disabled={!!busy}>
                    {busy === 'download' ? <CircularProgress size={14}/> : 'Download'}
                </Button>
                <Button size="small" variant="text" onClick={print} disabled={!!busy}>
                    {busy === 'print' ? <CircularProgress size={14}/> : 'Print'}
                </Button>
                <IconButton size="small" onClick={onClose} aria-label="Close"><Close fontSize="small"/></IconButton>
            </Box>
            <Box sx={{flex: 1, overflowY: 'auto', backgroundColor: '#f6f8fa', px: 2, py: 1}}>
                {response && <ResponseForm response={response}/>}
            </Box>
        </Popover>
    );
};

SubmissionPanel.propTypes = {
    anchorEl: PropTypes.object,
    response: PropTypes.object,
    formId: PropTypes.number,
    onClose: PropTypes.func.isRequired,
    showSubmittedBy: PropTypes.bool,
    title: PropTypes.node,
    phoneTitle: PropTypes.node
};

// Maria, 18 October 2026: a communication in a profile is opened with 'View', in the same viewer as a self-service
// submission, with 'Download' and 'Print' in the viewer
export const CommunicationView = ({communication}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const isPhone = usePhone();
    useViewerOpener(`communication-${communication.requestId}`, () => setAnchorEl(true));
    return (
        <>
            {!isPhone && <Button
                size="small"
                variant="text"
                onClick={event => {
                    event.stopPropagation();
                    setAnchorEl(event.currentTarget);
                }}
            >
                View
            </Button>}
            {isPhone && <PdfMenu formId={communication.formId} responseId={communication.requestId} name={communication.formName}/>}
            <Box onClick={event => event.stopPropagation()}>
                <SubmissionPanel
                    anchorEl={anchorEl}
                    response={anchorEl ? {id: communication.requestId, formName: communication.formName} : null}
                    formId={communication.formId}
                    onClose={() => setAnchorEl(null)}
                    title={`${communication.formName}${communication.sentAt ? ` - sent ${formatDate(communication.sentAt)}` : ''}`}
                />
            </Box>
        </>
    );
};

CommunicationView.propTypes = {
    communication: PropTypes.object.isRequired
};

// Maria, 19 October 2026: 'View' on a submitted form in a profile's Forms tab opens the form on the page, in the same
// viewer as a communication, with 'Download' and 'Print'
export const ResponseView = ({request, buttonSx}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const formName = request.form?.name ?? request.formName;
    const submittedAt = request.editedAt ?? request.submittedAt;
    const isPhone = usePhone();
    useViewerOpener(`request-${request.id}`, () => setAnchorEl(true));
    return (
        <>
            {!isPhone && <Button
                size="small"
                variant="contained"
                sx={buttonSx}
                onClick={event => {
                    event.stopPropagation();
                    setAnchorEl(event.currentTarget);
                }}
            >
                View
            </Button>}
            {isPhone && <PdfMenu formId={request.form?.id ?? request.formId} responseId={request.id} name={formName}/>}
            {/* 'contents', so the empty holder of the viewer takes no space beside the button */}
            <Box sx={{display: 'contents'}} onClick={event => event.stopPropagation()}>
                <SubmissionPanel
                    anchorEl={anchorEl}
                    response={anchorEl ? {id: request.id, formName} : null}
                    formId={request.form?.id ?? request.formId}
                    onClose={() => setAnchorEl(null)}
                    title={`${formName}${submittedAt ? ` - submitted ${formatDate(submittedAt)}` : ''}`}
                />
            </Box>
        </>
    );
};

ResponseView.propTypes = {
    request: PropTypes.object.isRequired,
    buttonSx: PropTypes.object
};

const Submissions = ({responses, formId, formName, showSubmittedBy}) => {
    const [viewing, setViewing] = useState({anchorEl: null, response: null});
    const isPhone = usePhone();
    const withApproval = responses.some(response => response.hasApproval || response.approvalStatus);
    const columns = submissionColumns(withApproval, showSubmittedBy, isPhone);
    return (
    <Box sx={{mx: 2, mb: 1.5, border: LINE, borderRadius: 2, overflow: 'hidden', width: isPhone ? 'auto' : 'fit-content', maxWidth: 'calc(100% - 32px)'}}>
        {/* Maria, 18 October 2026: no heading row, as the button above already says what the list is */}
        {responses.map((response, index) => (
            <Box
                key={response.id}
                data-testid="submission-row"
                onClick={isPhone ? () => setViewing({anchorEl: true, response}) : undefined}
                sx={{display: 'grid', gridTemplateColumns: columns, columnGap: 2, alignItems: 'center', px: 2, py: 0.5, borderTop: index === 0 ? 'none' : LINE, ...(isPhone ? {cursor: 'pointer'} : {})}}
            >
                <Box>{response.editedAt ? formatDate(response.editedAt) : ''}</Box>
                {showSubmittedBy && <Box sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{response.editedBy?.fullname ?? ''}</Box>}
                {withApproval && (
                    <Box>
                        {response.approvalStatus ? <AdmicityStatus {...getApprovalStatusInfo(response.approvalStatus)}/> : ''}
                    </Box>
                )}
                <Box sx={{display: 'flex', alignItems: 'center'}} onClick={event => event.stopPropagation()}>
                    {!isPhone && <Button size="small" variant="text" onClick={event => setViewing({anchorEl: event.currentTarget, response})}>View</Button>}
                    {isPhone && <PdfMenu formId={formId} responseId={response.id} name={response.formName ?? formName}/>}
                </Box>
            </Box>
        ))}
        <SubmissionPanel
            anchorEl={viewing.anchorEl}
            response={viewing.response}
            formId={formId}
            showSubmittedBy={showSubmittedBy}
            phoneTitle={viewing.response ? `${formName ?? ''}${viewing.response.editedAt ? ` - submitted ${formatDate(viewing.response.editedAt)}` : ''}` : null}
            onClose={() => setViewing({anchorEl: null, response: null})}
        />
    </Box>
    );
};

Submissions.propTypes = {
    responses: PropTypes.array.isRequired,
    formId: PropTypes.number,
    formName: PropTypes.string,
    showSubmittedBy: PropTypes.bool
};

const FormRow = ({form, personId, navigate, fetchFn, open, onToggle, isFirst, showSubmittedBy, showLinkedTo}) => {
    const [getResponses, {data: responses = [], isLoading, isUninitialized}] = fetchFn();

    useEffect(() => {
        getResponses({formId: form.formId, personId});
    }, [form.formId, personId, getResponses]);

    const count = Array.isArray(responses) ? responses.length : 0;
    const counting = isLoading || isUninitialized;

    return (
        <Box sx={{borderTop: isFirst ? 'none' : LINE, backgroundColor: open ? '#fafcfe' : 'transparent'}} data-testid="self-service-form">
            <Box sx={showLinkedTo
                ? {display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', px: 1.5, py: 0.5}
                : {display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1}}>
                {/* Maria, 18 October 2026: the buttons close to the form name, so the name column is only as wide as the longest names */}
                {/* on Student Self-service Forms the columns are narrower, so the list ends in line with its own tab */}
                {/* Maria, 19 October 2026: on Student Self-service Forms the student's name is in grey under the form name */}
                {/* Maria, 19 October 2026: the name takes the spare width, so the buttons sit at the right of the list */}
                {/* Maria, 19 October 2026: on Student Self-service Forms a name too long for the space ends in '...', with the
                    whole name in the hover, so the buttons stay on the same line */}
                <Box sx={{flex: '1 1 340px', minWidth: 0}} title={showLinkedTo ? (form.name ?? form.formName) : undefined}>
                    <Typography noWrap={showLinkedTo} sx={{fontWeight: 500, lineHeight: 1.3}}>{form.name ?? form.formName}</Typography>
                    {showLinkedTo && form.linkedPersonName && (
                        <Typography noWrap variant="body2" color="text.secondary" sx={{lineHeight: 1.3}}>{form.linkedPersonName}</Typography>
                    )}
                </Box>
                {/* Maria, 18 October 2026: on Student Self-service Forms, the student the form is about */}
                {form.viewOnly
                    // Maria, 19 October 2026: a form this member of staff may only look at has 'View', the same size as
                    // 'Open', in solid green, and opens the form for reading
                    ? (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`general/${form.formId}${linkedPersonQuery(form)}`)}
                            data-testid="view-only"
                            sx={{...OPEN_BUTTON_SIZE, backgroundColor: '#0DAD95', boxShadow: 'none', '&:hover': {backgroundColor: '#0A8572', boxShadow: 'none'}}}
                        >
                            View
                        </Button>
                    )
                    : (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`general/${form.formId}${linkedPersonQuery(form)}`)}
                            data-testid="submit-new-form"
                            sx={OPEN_BUTTON_SIZE}
                        >
                            {/* Maria, 19 October 2026: 'Open' */}
                            Open
                        </Button>
                    )}
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!counting && count === 0}
                    endIcon={open ? <ExpandLess/> : <ExpandMore/>}
                    onClick={onToggle}
                    // Maria, 18 October 2026: the size of its text, without a number; greyed out when there are none
                    // Maria, 19 October 2026: the normal size on every list
                    sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                    data-testid="previous-submissions"
                >
                    {/* Maria, 18 October 2026: 'Submissions' */}
                    Submissions
                </Button>
            </Box>
            <Collapse in={open && count > 0} unmountOnExit>
                <Submissions responses={responses} formId={form.formId} formName={form.name ?? form.formName} showSubmittedBy={showSubmittedBy}/>
            </Collapse>
        </Box>
    );
};

FormRow.propTypes = {
    form: PropTypes.object.isRequired,
    personId: PropTypes.number,
    navigate: PropTypes.func.isRequired,
    fetchFn: PropTypes.func.isRequired,
    open: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    isFirst: PropTypes.bool,
    showSubmittedBy: PropTypes.bool,
    showLinkedTo: PropTypes.bool
};

// Maria, 18 October 2026: the list starts under its own tab, or under the first tab when 'anchorTabTestId' names it.
// 'fill' (Maria, 19 October 2026): the list runs to the end of the page.
const SelfServiceForms = ({forms, isLoading, personId, navigate, fetchFn, openIndex, setOpenIndex, showSubmittedBy = true, endTabTestId, anchorTabTestId = 'tab-self-service-forms', showLinkedTo = false, fill = false, closedTop = false, bare = false}) => {
    const [indentRef, indent, measured, toLastTab, , openTab] = useIndentUnderTab(anchorTabTestId, endTabTestId);
    if (isLoading) {
        return <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}><CircularProgress size={24}/></Box>;
    }

    const rows = (
        <>
            {forms.length === 0
            ? <Typography color="text.secondary" sx={{px: 2, py: 2}}>No self-service forms are available.</Typography>
            : forms.map((form, index) => (
                <FormRow
                    key={`${form.formId}-${form.linkedStudentId ?? form.linkedEmployeeId ?? index}`}
                    form={form}
                    personId={personId}
                    navigate={navigate}
                    fetchFn={fetchFn}
                    open={openIndex === index}
                    onToggle={() => setOpenIndex(openIndex === index ? undefined : index)}
                    isFirst={index === 0}
                    showSubmittedBy={showSubmittedBy}
                    showLinkedTo={showLinkedTo}
                />
            ))}
        </>
    );
    // 'bare' (Maria, 19 October 2026): only the rows, for a box drawn round a row of tabs and its list together
    if (bare) {
        return <Box sx={{borderTop: LINE}}>{rows}</Box>;
    }

    return (
        <Card ref={indentRef} variant="outlined" sx={{visibility: measured ? 'visible' : 'hidden', ml: `${indent}px`, mr: fill ? 0 : 2, mb: 2, ...OUTLINED_BOX_SX, width: fill ? (endTabTestId && toLastTab ? `${toLastTab}px` : `calc(100% - ${indent}px)`) : toLastTab ? `${toLastTab}px` : 'fit-content', minWidth: fill ? 0 : 'fit-content', overflow: fill ? 'hidden' : undefined, maxWidth: fill ? `calc(100% - ${indent}px)` : `calc(100% - ${indent + 16}px)`}}>
            <TopLineAroundTab openTab={closedTop ? null : openTab}/>
            {rows}
        </Card>
    );
};

SelfServiceForms.propTypes = {
    forms: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    personId: PropTypes.number,
    navigate: PropTypes.func.isRequired,
    fetchFn: PropTypes.func.isRequired,
    openIndex: PropTypes.number,
    setOpenIndex: PropTypes.func.isRequired,
    showSubmittedBy: PropTypes.bool,
    endTabTestId: PropTypes.string,
    anchorTabTestId: PropTypes.string,
    showLinkedTo: PropTypes.bool,
    fill: PropTypes.bool,
    closedTop: PropTypes.bool,
    bare: PropTypes.bool
};

export default SelfServiceForms;
